import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTableLayout from '../../../components/PageTableLayout';
import apiService from '../../../services/apiService';
import { FaFilter, FaTimes, FaEye, FaPencilAlt } from 'react-icons/fa';
import { Button } from '../../../components/ui';

const breadcrumb = (
  <span>
    Home &nbsp;/&nbsp; <span className="text-gray-700">Webpages</span>
  </span>
);

const WebPages = () => {
  const navigate = useNavigate();
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    website: '',
    uri: '',
    status: ''
  });
  const [websites, setWebsites] = useState([]);
  const [limit, setLimit] = useState(10);
  const [isFiltered, setIsFiltered] = useState(false);

  const fetchData = useCallback(async (page, filterParams = {}, itemsPerPage = limit) => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page,
        limit: itemsPerPage,
        totalCount: true,
        populate: 'website',
        select: 'website,title,status,uri',
        select_website: 'title'
      });
      
      // Add any filter parameters
      Object.entries(filterParams).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });
      
      const response = await apiService.request(`/i/ims/frontpage?${params.toString()}`);
      
      setData(response.data);
      setTotalRecords(response.totalRecords);
      setCurrentPage(page);
      
      // Check if any columns are applied
      setIsFiltered(Object.values(filterParams).some(value => value !== ''));
    } catch (error) {
      console.error('Error fetching webpages:', error);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  // Fetch websites for filter dropdown
  const fetchWebsites = useCallback(async () => {
    try {
      const websiteData = await apiService.request('/i/ims/website?status=true&select=title');
      
      if (Array.isArray(websiteData)) {
        setWebsites(websiteData);
      } else {
        console.error('Website data is not an array:', websiteData);
        setWebsites([]);
      }
    } catch (err) {
      console.error('Error fetching websites:', err);
      setWebsites([]);
    }
  }, []);

  useEffect(() => {
    fetchData(1);
    fetchWebsites();
  }, [fetchData, fetchWebsites]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle filter submit
  const handleFilterSubmit = () => {
    fetchData(1, filters);
  };

  // Handle filter reset
  const handleFilterReset = () => {
    const resetFilters = {
      website: '',
      uri: '',
      status: ''
    };
    setFilters(resetFilters);
    fetchData(1, resetFilters);
  };

  // Handle page change
  const handlePageChange = (page) => {
    fetchData(page, filters);
  };

  // Handle limit change
  const handleLimitChange = (value) => {
    // Handle both direct value and event object
    const newLimit = Number(typeof value === 'object' ? value.target.value : value);
    setLimit(newLimit);
    // Reset to first page when changing limit
    fetchData(1, filters, newLimit);
  };

  // Handle add new button click
  const handleAddNew = () => {
    navigate('/web-pages/add');
  };

  const actionButtons = [
    {
      label: 'Add New',
      onClick: handleAddNew,
      className: 'bg-blue-600 hover:bg-blue-700'
    }
  ];

  const columns = [
    { 
      key: 'title', 
      title: 'Name',
      filterComponent: null // No filter for title column
    },
    { 
      key: 'website', 
      title: 'Website',
      render: (row) => row.website ? row.website.title : '-',
      filterComponent: (
        <select
          className="w-full border px-2 py-1 rounded text-xs"
          value={filters.website}
          onChange={(e) => handleFilterChange('website', e.target.value)}
          disabled={websites.length === 0}
        >
          <option value="">{websites.length === 0 ? 'Loading...' : 'All Websites'}</option>
          {websites.map((w) => (
            <option key={w.id} value={w.id}>{w.title}</option>
          ))}
        </select>
      )
    },
    { 
      key: 'uri', 
      title: 'Url',
      filterComponent: (
        <input 
          className="w-full border px-2 py-1 rounded text-xs" 
          placeholder="Filter by URI"
          value={filters.uri}
          onChange={(e) => handleFilterChange('uri', e.target.value)}
        />
      )
    },
    { 
      key: 'status', 
      title: 'Status',
      render: (row) => (
        <span className={`px-2 py-1 rounded-full text-xs ${row.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {row.status ? 'Active' : 'Inactive'}
        </span>
      ),
      filterComponent: (
        <select 
          className="w-full border px-2 py-1 rounded text-xs"
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      )
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (row) => (
        <div className="flex space-x-2">
          <Button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/web-pages/view/${row.id}`);
            }}
            variant="outline"
            size="xs"
            title="View"
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
          >
            <FaEye className="h-4 w-4" />
          </Button>
          <Button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/web-pages/edit/${row.id}`);
            }}
            variant="outline"
            size="xs"
            title="Edit"
            className="text-amber-600 border-amber-600 hover:bg-amber-50"
          >
            <FaPencilAlt className="h-4 w-4" />
          </Button>
        </div>
      ),
      filterComponent: (
        <div className="flex space-x-1">
          <Button
            variant="outline"
            size="xs"
            className="text-blue-600 border-blue-600 hover:bg-blue-50"
            onClick={handleFilterSubmit}
            disabled={!filters.uri&&!filters.website&&!(["true","false"].includes(filters.status))}
          >
            <FaFilter className="h-3 w-3 mr-1" />
            Filter
          </Button>
          <Button
            variant="outline"
            size="xs"
            className="text-red-600 border-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleFilterReset}
            disabled={!isFiltered}
          >
            <FaTimes className="h-3 w-3 mr-1" />
            Reset
          </Button>
        </div>
      )
    }
  ];

  const handleView = (row) => {
    navigate(`/web-pages/view/${row.id}`);
  };

  const handleEdit = (row) => {
    navigate(`/web-pages/edit/${row.id}`);
  };


  return (
    <PageTableLayout
      title="Webpages"
      breadcrumb={breadcrumb}
      columns={columns}
      data={data || []}
      actions={actionButtons}
      loading={loading}
      pagination={{
        currentPage,
        totalRecords,
        totalPages: Math.ceil(totalRecords / limit),
        limit,
        onLimitChange: handleLimitChange
      }}
      onPageChange={handlePageChange}
      showFiltersInTable={true}
    />
  );
};

export default WebPages;
